import {
  Box,
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useExpanded, useTable } from 'react-table'
import CustomCell from '../../../../components/Table/TableCells/CustomCell/CustomCell'
import { colors } from '../../../../utils/theme/styles'
const defaultColumn = {
  Cell: CustomCell,
}
function Table({
  columns: userColumns,
  data,
  renderRowSubComponent,
  headerColor,
  headerVariant,
  normalTextVariant,
  expandedTextVariant,
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = useTable(
    {
      columns: userColumns,
      defaultColumn: defaultColumn,
      data,
    },
    useExpanded, // We can useExpanded to track the expanded state
    // for sub components too!
  )

  return (
    <>
      <table style={{ width: '100%' }} {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr
              key={headerGroup}
              style={{
                width: '100%',
                background: headerColor,
                color: 'white',
                fontWeight: 'bold',
                position: 'sticky',
                zIndex: 0,
                top: 0,
              }}
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column) => (
                <th key={column.id} {...column.getHeaderProps()}>
                  {column.render('Header', {
                    variant: headerVariant,
                    color: 'white',
                  })}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          style={{
            border: '1px solid gray',
            textAlign: 'center',
            width: '100%',
          }}
          {...getTableBodyProps()}
        >
          {rows.map((row) => {
            prepareRow(row)
            return (
              // Use a React.Fragment here so the table markup is still valid
              <React.Fragment key={row} {...row.getRowProps()}>
                <tr style={{ border: '1px solid gray', textAlign: 'center' }}>
                  {row.cells.map((cell) => {
                    return (
                      <td key={cell} {...cell.getCellProps()}>
                        {cell.render('Cell', {
                          textVariant: row.isExpanded
                            ? expandedTextVariant
                            : normalTextVariant,
                        })}
                      </td>
                    )
                  })}
                </tr>
                {/*
                      If the row is in an expanded state, render a row with a
                      column that fills the entire length of the table.
                    */}
                {row.isExpanded ? (
                  <tr>
                    <td colSpan={visibleColumns.length}>
                      {/*
                            Inside it, call our renderRowSubComponent function. In reality,
                            you could pass whatever you want as props to
                            a component like this, including the entire
                            table instance. But for this example, we'll just
                            pass the row
                          */}
                      {renderRowSubComponent({ row })}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

Table.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  expandedTextVariant: PropTypes.string,
  headerColor: PropTypes.string,
  headerVariant: PropTypes.string,
  normalTextVariant: PropTypes.string,
  renderRowSubComponent: PropTypes.func,
}
const Cell = ({ row }) => (
  // Use Cell to render an expander for each row.
  // We can use the getToggleRowExpandedProps prop-getter
  // to build the expander.
  <div {...row.getToggleRowExpandedProps()}>
    {row.isExpanded ? (
      <FontAwesomeIcon icon={faEyeSlash} />
    ) : (
      <FontAwesomeIcon icon={faEye} />
    )}
  </div>
)

Cell.propTypes = {
  row: PropTypes.shape({
    getToggleRowExpandedProps: PropTypes.func,
    isExpanded: PropTypes.bool,
  }),
}
const tradeColumns = [
  {
    // Make an expander cell
    Header: () => null, // No header
    id: 'expander', // It needs an ID
    Cell: Cell,
  },
  {
    Header: (props) => <Text {...props}>Nombre comercio</Text>,
    accessor: 'sNombreComercio',
    id: 'sNombreComercio',
  },
  {
    Header: (props) => <Text {...props}>Razon social</Text>,
    accessor: 'sRazonSocialComercio',
    id: 'sRazonSocialComercio',
  },
  {
    Header: (props) => <Text {...props}>Monto</Text>,
    accessor: 'nMontoComercio',
    id: 'nMontoComercio',
    type: 'currency',
  },
]
const localColumns = [
  {
    // Make an expander cell
    Header: () => null, // No header
    id: 'expander', // It needs an ID
    Cell: Cell,
  },
  {
    Header: (props) => <Text {...props}>Nombre local</Text>,
    accessor: 'sNombreLocal',
    id: 'sNombreLocal',
  },
  {
    Header: (props) => <Text {...props}>Cantidad de transacciones</Text>,
    accessor: 'nCantTxLocal',
    id: 'nCantTxLocal',
  },
  {
    Header: (props) => <Text {...props}>Monto total</Text>,
    accessor: 'nMontoLocal',
    id: 'nMontoLocal',
    type: 'currency',
  },
]
const transactionColumns = [
  {
    Header: (props) => <Text {...props}>Fecha transacción</Text>,
    accessor: 'dFechaTx',
    id: 'dFechaTx',
    type: 'date',
  },
  {
    Header: (props) => <Text {...props}>Tipo transacción</Text>,
    accessor: 'sTipoTx',
    id: 'sTipoTx',
  },
  {
    Header: (props) => <Text {...props}>Estado</Text>,
    accessor: 'sEstado',
    id: 'sEstado',
  },
  {
    Header: (props) => <Text {...props}>Cant.</Text>,
    accessor: 'nCantTx',
    id: 'nCantTx',
  },
  {
    Header: (props) => <Text {...props}>Monto.</Text>,
    accessor: 'nMonto',
    id: 'nMonto',
    type: 'currency',
  },
]
export default function ConsumptionReport({ data }) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const secondTable = useCallback((secondRow) => {
    return (
      <Box alignSelf="self-end" pl="5%">
        <Table
          normalTextVariant={'subtitle3'}
          headerVariant="subtitle3"
          headerColor={colors.gray[400]}
          data={secondRow?.row.original.lTx}
          columns={transactionColumns}
        />
      </Box>
    )
  }, [])
  const firstTable = useCallback((firstRow) => {
    return (
      <Box alignSelf="self-end" pl="5%">
        <Table
          normalTextVariant={'body2'}
          expandedTextVariant="bodyActive2"
          headerVariant="subtitle2"
          headerColor={colors.gray[700]}
          data={firstRow?.row.original.lTxLocal}
          columns={localColumns}
          renderRowSubComponent={secondTable}
        />
      </Box>
    )
  }, [])
  return (
    <>
      <Box
        h="40px"
        onClick={onOpen}
        paddingRight={10}
        paddingLeft={10}
        borderRadius={50}
        backgroundColor="#EFA329"
        style={{ cursor: 'pointer' }}
      >
        <HStack h="100%" alignItems="center" justifyContent="center">
          <FontAwesomeIcon color="#fff" icon={faEye} />
          <Text variant="semibold" color="#fff">
            Ver informe
          </Text>
        </HStack>
      </Box>

      <Modal size="4xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bgColor={colors.secondary.base} color={colors.gray[50]}>
            Reporte de consumo de empresa
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Table
              normalTextVariant={'body1'}
              expandedTextVariant="bodyActive"
              headerVariant="subtitle1"
              headerColor={colors.gray[900]}
              data={data.lTxComercio}
              columns={tradeColumns}
              renderRowSubComponent={firstTable}
            />
          </ModalBody>

          <ModalFooter w="100%">
            <Center w="100%">
              <Button w="324px" onClick={onClose}>
                Cerrar
              </Button>
            </Center>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

ConsumptionReport.propTypes = {
  data: PropTypes.shape({
    lTxComercio: PropTypes.any,
  }),
}
